import { useMutation, UseMutationResult } from '@tanstack/react-query'
import { content, OpalApiResponseDocument } from '@opallabs/api/v2'

type UseCreateContentParams = {
  momentId: string
  postTypeId?: string
  serviceId: string
  // When present, the server runs `Moments::ServiceObjects::PostDuplicator`
  // and clones the source post (attributes, schedule, assets, labels, etc.).
  // Used by Content mode's duplicate-content flow so the new post flows
  // back into Redux via the same response-deserialize path that inline
  // content creation uses — no Backbone or bulk moment refetch required.
  duplicatedFromMomentId?: string
  duplicatedFromPostId?: string
  name?: string
  position?: number | null
}

const createContent = ({
  momentId,
  postTypeId,
  serviceId,
  duplicatedFromMomentId,
  duplicatedFromPostId,
  name,
  position,
}: UseCreateContentParams) =>
  content.create({
    requestBody: {
      data: {
        attributes: {
          ...(name !== undefined && name.length > 0 && { name }),
          ...(position !== undefined && position !== null && { position }),
        },
        relationships: {
          service: { data: { id: serviceId, type: 'service' } },
          moment: { data: { id: momentId, type: 'moment' } },
          ...(postTypeId !== undefined && {
            post_type: { data: { id: postTypeId, type: 'post_type' } },
          }),
          ...(duplicatedFromMomentId !== undefined && {
            duplicated_from_moment: {
              data: { id: duplicatedFromMomentId, type: 'moment' },
            },
          }),
          ...(duplicatedFromPostId !== undefined && {
            duplicated_from_post: {
              data: { id: duplicatedFromPostId, type: 'content' },
            },
          }),
        },
        type: 'content',
      },
    },
  })

export const useCreateContent = (): UseMutationResult<
  OpalApiResponseDocument<'CreateContentV2', 201>,
  unknown,
  UseCreateContentParams,
  unknown
> => {
  return useMutation({ mutationFn: createContent })
}
